import React, { useRef, useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PenTool, RotateCcw, Check, X } from 'lucide-react';

interface SignaturePadProps {
  onSignature: (dataUrl: string) => void;
  onCancel: () => void;
  title?: string;
  placeholder?: string;
  isSubmitting?: boolean;
}

export function SignaturePad({ 
  onSignature, 
  onCancel, 
  title = "Store Manager Signature",
  placeholder = "Please sign in the box below",
  isSubmitting = false 
}: SignaturePadProps) {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Check if signature pad is empty
  const checkIsEmpty = () => {
    if (sigCanvas.current) {
      setIsEmpty(sigCanvas.current.isEmpty());
    }
  };

  // Clear signature
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
    }
  };

  // Save signature
  const saveSignature = () => {
    console.log("Save signature clicked, isEmpty:", isEmpty);
    if (sigCanvas.current && !isEmpty) {
      // Validate minimum stroke length
      const canvas = sigCanvas.current.getCanvas();
      const ctx = canvas.getContext('2d');
      const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData?.data || [];
      
      // Count non-white pixels as a simple validation
      let nonWhitePixels = 0;
      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i] < 250 || pixels[i + 1] < 250 || pixels[i + 2] < 250) {
          nonWhitePixels++;
        }
      }
      
      // Require at least 100 pixels (roughly 10x10 area)
      if (nonWhitePixels < 100) {
        console.log("Signature too small - needs more strokes");
        setIsEmpty(true); // Force re-draw
        return;
      }
      
      const dataUrl = sigCanvas.current.toDataURL('image/png');
      console.log("Generated signature data URL:", dataUrl.substring(0, 50) + "...");
      onSignature(dataUrl);
    } else {
      console.log("Cannot save signature - canvas ref or isEmpty:", sigCanvas.current, isEmpty);
    }
  };

  // Handle touch/pen events
  const handleBegin = () => {
    console.log("Signature drawing began");
    setIsEmpty(false);
  };

  const handleEnd = () => {
    console.log("Signature drawing ended");
    checkIsEmpty();
  };

  useEffect(() => {
    // Set up signature canvas options - re-run when component mounts or when signature pad becomes visible
    const timer = setTimeout(() => {
      if (sigCanvas.current) {
        const canvas = sigCanvas.current.getCanvas();
        canvas.style.touchAction = 'none';
        (canvas.style as any).WebkitTouchCallout = 'none';
        (canvas.style as any).webkitUserSelect = 'none';
        canvas.style.userSelect = 'none';
        canvas.style.backgroundColor = 'white';
        canvas.style.cursor = 'crosshair';
        canvas.style.pointerEvents = 'auto';
        
        // Ensure canvas is focusable and ready for touch
        canvas.setAttribute('tabindex', '0');
        
        console.log("Canvas initialized successfully:", {
          width: canvas.width,
          height: canvas.height,
          touchAction: canvas.style.touchAction,
          pointerEvents: canvas.style.pointerEvents
        });
        
        // Force focus on canvas to ensure it's ready for input
        canvas.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  console.log("SignaturePad rendering, isEmpty:", isEmpty);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-white shadow-xl">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <PenTool className="h-5 w-5" />
            {title}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{placeholder}</p>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Signature Canvas */}
          <div 
            className="border-2 border-solid border-gray-300 rounded-lg bg-white relative overflow-hidden"
            style={{ touchAction: 'none', WebkitTouchCallout: 'none' }}
          >
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                width: window.innerWidth > 500 ? 450 : window.innerWidth - 80,
                height: 180,
                className: 'signature-canvas block',
                style: { 
                  touchAction: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  backgroundColor: 'white',
                  width: '100%',
                  height: '180px',
                  display: 'block',
                  pointerEvents: 'auto'
                }
              }}
              backgroundColor="white"
              penColor="black"
              minWidth={1.5}
              maxWidth={3}
              velocityFilterWeight={0.7}
              onBegin={handleBegin}
              onEnd={handleEnd}
            />
            
            {/* Placeholder text when empty */}
            {isEmpty && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="text-center text-gray-400">
                  <PenTool className="h-6 w-6 mx-auto mb-1 opacity-50" />
                  <p className="text-xs">Draw your signature here</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {/* Clear Button */}
            <Button
              variant="outline"
              onClick={clearSignature}
              disabled={isEmpty || isSubmitting}
              className="flex-1"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Clear
            </Button>
            
            {/* Cancel Button */}
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            {/* Save Button */}
            <Button
              onClick={saveSignature}
              disabled={isEmpty || isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 mr-2 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// CSS for signature canvas (add to global styles)
export const signaturePadStyles = `
.signature-canvas {
  border-radius: 0.5rem;
  cursor: crosshair;
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

@media (pointer: coarse) {
  .signature-canvas {
    cursor: default;
  }
}

/* Ensure signature modal is above everything */
.signature-modal {
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
`;